import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  InputAdornment
} from "@mui/material";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { ProductStatus } from "../enums/products";
import type { FilterFormState } from "./types";
import { buildProductListRequest } from "./types";
import { useGetCategoryList } from "../../../hooks/api/useCategory";
import { getLeafCategories } from "../../Category/utils/categoryUtils";
import type { Category } from "../../Category/types/categoryTypes";
import { CATEGORY_STATUS } from "../../Category/enums/categoryEnums";
import { RootState } from "../../../store/store";
import {
  updateProductListFilterForm,
  setProductListRequest,
  resetProductListFilters,
  setCompanyAdminProductListStoreFilter
} from "../../../store/slices/productSlice";
import CompanyAdminInventoryStoreBar from "../CompanyAdminInventoryStoreBar";
import { isUserCompanyAdmin } from "../../../hooks/api/useProductItem";

export type ProductListFiltersProps = {
  onApplySuccess?: () => void;
  onClearSuccess?: () => void;
};

const ProductListFilters: React.FC<ProductListFiltersProps> = ({
  onApplySuccess,
  onClearSuccess
}) => {
  const dispatch = useDispatch();
  const { t } = useKivunovaTranslation();
  const filters = useSelector(
    (state: RootState) => state.products.productListFilterForm
  );
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyAdminProductStoreByCompany = useSelector(
    (state: RootState) => state.products.companyAdminProductListStoreIdByCompany
  );
  const companyAdminListStoreId = useMemo(() => {
    if (!isUserCompanyAdmin(userDetails) || !userDetails?.companyId?.trim()) {
      return null;
    }
    const id =
      companyAdminProductStoreByCompany[userDetails.companyId.trim()]?.trim();
    return id && id.length > 0 ? id : null;
  }, [userDetails, companyAdminProductStoreByCompany]);

  const handleCompanyAdminCatalogStoreChange = useCallback(
    (storeId: string | null) => {
      const cid = userDetails?.companyId?.trim();
      if (!cid) return;
      dispatch(
        setCompanyAdminProductListStoreFilter({ companyId: cid, storeId })
      );
    },
    [dispatch, userDetails?.companyId]
  );

  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const getCategoryListMutation = useGetCategoryList();

  const onFilterChange = <K extends keyof FilterFormState>(
    key: K,
    value: FilterFormState[K]
  ) => {
    dispatch(updateProductListFilterForm({ key, value }));
  };

  const onApply = () => {
    dispatch(
      setProductListRequest(buildProductListRequest(filters, { page: 0 }))
    );
    onApplySuccess?.();
  };

  const onClear = () => {
    dispatch(resetProductListFilters());
    const cid = userDetails?.companyId?.trim();
    if (cid && isUserCompanyAdmin(userDetails)) {
      dispatch(
        setCompanyAdminProductListStoreFilter({ companyId: cid, storeId: null })
      );
    }
    onClearSuccess?.();
  };

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const list = await getCategoryListMutation.mutateAsync({
          status: CATEGORY_STATUS.ACTIVE
        });
        if (!cancelled) setCategoryList(list ?? []);
      } catch {
        if (!cancelled) setCategoryList([]);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        {t("filter-products", "Filter products")}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "1fr 1fr",
          md: "1fr 1fr 1fr"
        }}
        gap={2}
      >
        {isUserCompanyAdmin(userDetails) && userDetails?.companyId?.trim() ? (
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "1 / -1" }
            }}
          >
            <CompanyAdminInventoryStoreBar
              variant="inline"
              selectedStoreId={companyAdminListStoreId}
              onSelectedStoreIdChange={handleCompanyAdminCatalogStoreChange}
              inlineTooltip={t(
                "inventory-store-filter-catalog-hint",
                "Filters the product catalog by store when applicable. Clear the selection to include all stores."
              )}
            />
          </Box>
        ) : null}
        <TextField
          size="small"
          label={t("name", "Name")}
          value={filters.name}
          onChange={(e) => onFilterChange("name", e.target.value)}
          placeholder={t("partial-match", "Partial match")}
          fullWidth
          inputProps={{ "data-testid": "product-list-filter-name" }}
        />
        <TextField
          size="small"
          label={t("brand", "Brand")}
          value={filters.brandName}
          onChange={(e) => onFilterChange("brandName", e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          select
          label={t("category", "Category")}
          value={filters.categoryPath}
          onChange={(e) => onFilterChange("categoryPath", e.target.value)}
          fullWidth
          disabled={getCategoryListMutation.isPending}
        >
          <MenuItem value="">{t("all-categories", "All categories")}</MenuItem>
          {getLeafCategories(categoryList).map((cat) => (
            <MenuItem key={cat.id} value={cat.path ?? cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label={t("status", "Status")}
          value={filters.status}
          onChange={(e) =>
            onFilterChange("status", e.target.value as ProductStatus | "")
          }
          fullWidth
        >
          <MenuItem value="">
            {t("all-excl-deleted", "All (excl. deleted)")}
          </MenuItem>
          <MenuItem value={ProductStatus.ACTIVE}>
            {t("active", "Active")}
          </MenuItem>
          <MenuItem value={ProductStatus.INACTIVE}>
            {t("inactive", "Inactive")}
          </MenuItem>
          <MenuItem value={ProductStatus.DISCONTINUED}>
            {t("discontinued", "Discontinued")}
          </MenuItem>
          <MenuItem value={ProductStatus.DELETED}>
            {t("deleted", "Deleted")}
          </MenuItem>
        </TextField>
        <TextField
          size="small"
          label={t("min-sales-price", "Min sales price")}
          type="number"
          value={filters.minSalesPrice}
          onChange={(e) => onFilterChange("minSalesPrice", e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
          fullWidth
        />
        <TextField
          size="small"
          label={t("max-sales-price", "Max sales price")}
          type="number"
          value={filters.maxSalesPrice}
          onChange={(e) => onFilterChange("maxSalesPrice", e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
          fullWidth
        />
        <TextField
          size="small"
          label={t("min-quantity-in-stock", "Min quantity in stock")}
          type="number"
          value={filters.minQuantityInStock}
          onChange={(e) => onFilterChange("minQuantityInStock", e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label={t("max-quantity-in-stock", "Max quantity in stock")}
          type="number"
          value={filters.maxQuantityInStock}
          onChange={(e) => onFilterChange("maxQuantityInStock", e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label={t("expiration-date", "Expiration date")}
          type="date"
          value={filters.expirationDate}
          onChange={(e) => onFilterChange("expirationDate", e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          size="small"
          label={t("expiration-from", "Expiration from")}
          type="date"
          value={filters.expirationDateFrom}
          onChange={(e) => onFilterChange("expirationDateFrom", e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          size="small"
          label={t("expiration-to", "Expiration to")}
          type="date"
          value={filters.expirationDateTo}
          onChange={(e) => onFilterChange("expirationDateTo", e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={onApply}
          sx={{
            backgroundColor: BRAND_COLOR,
            textTransform: "none",
            "&:hover": { backgroundColor: BRAND_HOVER }
          }}
        >
          {t("apply", "Apply")}
        </Button>
        <Button
          variant="outlined"
          onClick={onClear}
          sx={{
            textTransform: "none",
            color: BRAND_COLOR,
            borderColor: BRAND_COLOR,
            "&:hover": {
              borderColor: BRAND_HOVER,
              backgroundColor: `${BRAND_COLOR}10`
            }
          }}
        >
          {t("clear", "Clear")}
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductListFilters;
